/**
 * Tagada Rx — storefront wizard.
 *
 * Two paths:
 *  - Template (fast): pick a template → brand (logo / colors) → subdomain →
 *    pixels → publish. Publishing auto-deploys the chosen template with the
 *    right funnel on slug.tgdcare.com.
 *  - Headless (build your own): guidance for building with the Headless SDK.
 *    LegitScript constraint: the site must still be hosted on the Tagada
 *    subdomain. Own root domain = coming soon.
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Code2, ExternalLink, LayoutTemplate, Loader2, Puzzle, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { ImageUpload } from '@/components/form/image-upload';
import { RxPageShell, RxStatementDescriptorCard, RxSupportMailboxCard } from '@/components/rx';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { cn } from '@/lib/utils';
import {
  useRxIntegrationStatus,
  useRxPublishStorefront,
  useRxSaveStorefront,
  useRxStorefront,
  useRxStorefrontPlugins,
  useRxStorefrontTemplates,
} from '@/lib/api/rx';

const DOCS_BASE = import.meta.env.VITE_DOCS_URL || 'https://docs.tagada.io';

type WizardMode = 'template' | 'headless';

/** `templateId` value for "bring your own plugin" (mirrors the server constant). */
const CUSTOM_TEMPLATE_ID = 'custom';

export default function RxStorefrontPage() {
  const { t } = useTranslation('rx');
  const { id: storeId, storeRoutes } = useStoreNavigate();
  const { toast } = useToast();
  const integration = useRxIntegrationStatus(storeId);
  const profileQuery = useRxStorefront(storeId);
  const templatesQuery = useRxStorefrontTemplates();
  const save = useRxSaveStorefront();
  const publish = useRxPublishStorefront();

  const [mode, setMode] = React.useState<WizardMode>('template');
  const [templateId, setTemplateId] = React.useState('tgdcare');
  const [pluginId, setPluginId] = React.useState<string | null>(null);
  const customSelected = templateId === CUSTOM_TEMPLATE_ID;
  const pluginsQuery = useRxStorefrontPlugins(customSelected);
  const [slug, setSlug] = React.useState('');
  const [brandName, setBrandName] = React.useState('');
  const [tagline, setTagline] = React.useState('');
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = React.useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = React.useState('#39543F');
  const [accentColor, setAccentColor] = React.useState('#B4854B');
  const [metaPixelId, setMetaPixelId] = React.useState('');
  const [gtmId, setGtmId] = React.useState('');
  const [siteVerifications, setSiteVerifications] = React.useState('');

  React.useEffect(() => {
    const data = profileQuery.data;
    if (!data) return;
    setTemplateId(data.templateId);
    setPluginId(data.pluginId);
    setSlug(data.slug);
    setBrandName(data.brandName);
    setTagline(data.tagline);
    setLogoUrl(data.logoUrl);
    setFaviconUrl(data.faviconUrl);
    setPrimaryColor(data.primaryColor);
    setAccentColor(data.accentColor);
    setMetaPixelId(data.metaPixelId);
    setGtmId(data.gtmId);
    setSiteVerifications(
      data.siteVerifications
        .map((v) =>
          v.kind === 'meta' ? `<meta name="${v.name}" content="${v.content}" />` : v.value,
        )
        .join('\n'),
    );
  }, [profileQuery.data]);

  const rxActive = integration.data?.state === 'active';
  const apex = profileQuery.data?.apex ?? 'tgdcare.com';
  const hostname = slug ? `${slug.toLowerCase().trim()}.${apex}` : null;
  const templates = templatesQuery.data ?? [];

  const persist = async () => {
    await save.mutateAsync({
      storeId,
      slug: slug || undefined,
      templateId,
      pluginId: customSelected ? pluginId : null,
      brandName: brandName || undefined,
      tagline: tagline || undefined,
      logoUrl,
      faviconUrl,
      primaryColor,
      accentColor,
      metaPixelId,
      gtmId,
      siteVerifications: siteVerifications.split('\n').filter((line) => line.trim()),
    });
  };

  const handleSave = async () => {
    try {
      await persist();
      toast({ title: t('storefront.toasts.savedTitle') });
      await profileQuery.refetch();
    } catch (err) {
      toast({
        title: t('storefront.toasts.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('storefront.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const handlePublish = async () => {
    try {
      await persist();
      const result = await publish.mutateAsync({ storeId });
      toast({
        title: t('storefront.toasts.publishedTitle'),
        description: result.url ?? t('storefront.toasts.publishedFallback'),
      });
      await profileQuery.refetch();
    } catch (err) {
      toast({
        title: t('storefront.toasts.publishFailedTitle'),
        description: err instanceof Error ? err.message : t('storefront.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const subdomainCard = (step: string) => (
    <Card>
      <CardHeader>
        <CardTitle>{t('storefront.steps.subdomain.title', { step })}</CardTitle>
        <CardDescription>
          {t('storefront.steps.subdomain.description', {
            url: `https://${hostname ?? `your-brand.${apex}`}`,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor="rx-slug">{t('storefront.steps.subdomain.label')}</Label>
        <div className="flex items-center gap-2">
          <Input
            id="rx-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder={t('storefront.steps.subdomain.placeholder')}
            className="max-w-xs"
          />
          <span className="text-muted-foreground text-sm">.{apex}</span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <RxPageShell
      title={t('storefront.title')}
      description={t('storefront.description')}
    >
      {!rxActive ? (
        <div className="text-muted-foreground rounded-lg border px-4 py-6 text-sm">
          {t('storefront.notActive')}
        </div>
      ) : profileQuery.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('storefront.loading')}
        </div>
      ) : (
        <div className="space-y-4">
          {profileQuery.data?.published && profileQuery.data.url ? (
            <div className="flex items-center justify-between rounded-lg border px-4 py-3">
              <div className="flex items-center gap-2">
                <Badge>{t('storefront.liveBadge')}</Badge>
                <a
                  href={profileQuery.data.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium underline"
                >
                  {profileQuery.data.url}
                </a>
              </div>
            </div>
          ) : null}

          {profileQuery.data?.published && !profileQuery.data.paymentFlowId ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              {t('storefront.noPaymentFlow')}
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setMode('template')}
              className={cn(
                'rounded-lg border p-4 text-left transition-colors',
                mode === 'template' ? 'border-primary ring-primary/30 ring-2' : 'hover:border-foreground/30',
              )}
            >
              <div className="flex items-center gap-2 font-medium">
                <LayoutTemplate className="h-4 w-4" />
                {t('storefront.modes.templateTitle')}
                <Badge variant="secondary">{t('storefront.modes.templateBadge')}</Badge>
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                {t('storefront.modes.templateDescription')}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setMode('headless')}
              className={cn(
                'rounded-lg border p-4 text-left transition-colors',
                mode === 'headless' ? 'border-primary ring-primary/30 ring-2' : 'hover:border-foreground/30',
              )}
            >
              <div className="flex items-center gap-2 font-medium">
                <Code2 className="h-4 w-4" />
                {t('storefront.modes.headlessTitle')}
                <Badge variant="outline">{t('storefront.modes.headlessBadge')}</Badge>
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                {t('storefront.modes.headlessDescription')}
              </p>
            </button>
          </div>

          {mode === 'template' ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{t('storefront.steps.template.title')}</CardTitle>
                  <CardDescription>
                    {t('storefront.steps.template.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 lg:grid-cols-3">
                  {templatesQuery.isLoading ? (
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('storefront.steps.template.loading')}
                    </div>
                  ) : (
                    templates.map((tpl) => {
                      const selected = templateId === tpl.id;
                      return (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => setTemplateId(tpl.id)}
                          className={cn(
                            'group flex flex-col overflow-hidden rounded-xl border text-left transition-all',
                            selected
                              ? 'border-primary ring-primary/30 shadow-md ring-2'
                              : 'hover:border-foreground/30 hover:shadow-sm',
                          )}
                        >
                          <div className="bg-muted relative aspect-video w-full overflow-hidden border-b">
                            <img
                              src={`/images/rx-templates/${tpl.id}.webp`}
                              alt={t('storefront.steps.template.previewAlt', { name: tpl.name })}
                              loading="lazy"
                              className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            {selected ? (
                              <span className="bg-primary text-primary-foreground absolute right-2 top-2 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium shadow">
                                <Check className="h-3 w-3" />
                                {t('storefront.steps.template.selected')}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex flex-1 flex-col p-4">
                            <span className="font-medium">{tpl.name}</span>
                            <p className="text-muted-foreground mt-1 flex-1 text-sm">
                              {t(`storefront.templates.${tpl.id}`, { defaultValue: tpl.description })}
                            </p>
                            <div className="mt-3 flex items-center justify-between gap-2">
                              <div className="flex flex-wrap gap-1">
                                {tpl.verticals.map((v) => (
                                  <Badge key={v} variant="secondary" className="text-xs">
                                    {t(`storefront.verticals.${v}`, { defaultValue: v })}
                                  </Badge>
                                ))}
                              </div>
                              <a
                                href={tpl.demoUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex flex-shrink-0 items-center gap-1 text-xs font-medium underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {t('storefront.steps.template.viewDemo')}
                              </a>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                  {templatesQuery.isLoading ? null : (
                    <button
                      type="button"
                      onClick={() => setTemplateId(CUSTOM_TEMPLATE_ID)}
                      className={cn(
                        'flex flex-col rounded-xl border border-dashed p-4 text-left transition-all',
                        customSelected
                          ? 'border-primary ring-primary/30 shadow-md ring-2'
                          : 'hover:border-foreground/30 hover:shadow-sm',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 font-medium">
                          <Puzzle className="h-4 w-4" />
                          {t('storefront.steps.template.custom.title')}
                        </span>
                        {customSelected ? <Check className="text-primary h-4 w-4" /> : null}
                      </div>
                      <p className="text-muted-foreground mt-2 flex-1 text-sm">
                        {t('storefront.steps.template.custom.description')}
                      </p>
                      <a
                        href={`${DOCS_BASE}/developer-tools/rx/plugin-sdk#bring-your-own-storefront-plugin`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="mt-3 flex items-center gap-1 text-xs font-medium underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {t('storefront.steps.template.custom.docs')}
                      </a>
                    </button>
                  )}
                  {customSelected ? (
                    <div className="space-y-2 lg:col-span-3">
                      <Label htmlFor="rx-custom-plugin">
                        {t('storefront.steps.template.custom.pluginLabel')}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Select value={pluginId ?? ''} onValueChange={(value) => setPluginId(value)}>
                          <SelectTrigger id="rx-custom-plugin" className="max-w-xl">
                            <SelectValue
                              placeholder={
                                pluginsQuery.isLoading
                                  ? t('storefront.steps.template.custom.loading')
                                  : t('storefront.steps.template.custom.pluginPlaceholder')
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {(pluginsQuery.data ?? []).map((plugin) => (
                              <SelectItem key={plugin.pluginId} value={plugin.pluginId}>
                                {plugin.name} · {plugin.pluginId} · v{plugin.version}
                                {plugin.owned ? '' : ` · ${t('storefront.steps.template.custom.public')}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => pluginsQuery.refetch()}
                          disabled={pluginsQuery.isFetching}
                          aria-label={t('storefront.steps.template.custom.refresh')}
                        >
                          <RefreshCw className={cn('h-4 w-4', pluginsQuery.isFetching && 'animate-spin')} />
                        </Button>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {pluginsQuery.data && pluginsQuery.data.length === 0
                          ? t('storefront.steps.template.custom.empty')
                          : t('storefront.steps.template.custom.hint')}
                      </p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('storefront.steps.brand.title')}</CardTitle>
                  <CardDescription>{t('storefront.steps.brand.description')}</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="rx-brand-name">{t('storefront.steps.brand.nameLabel')}</Label>
                    <Input
                      id="rx-brand-name"
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="rx-tagline">{t('storefront.steps.brand.taglineLabel')}</Label>
                    <Input id="rx-tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('storefront.steps.brand.logoLabel')}</Label>
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="mb-2 h-12 w-auto rounded border bg-white p-1" />
                    ) : null}
                    <ImageUpload url={logoUrl} onImageAvailable={setLogoUrl} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-favicon">{t('storefront.steps.brand.faviconLabel')}</Label>
                    {faviconUrl ? (
                      <img src={faviconUrl} alt="" className="mb-2 h-8 w-8 rounded border bg-white p-1" />
                    ) : null}
                    <ImageUpload
                      url={faviconUrl}
                      onImageAvailable={setFaviconUrl}
                      accept="image/png,image/x-icon"
                    />
                    <Input
                      id="rx-favicon"
                      value={faviconUrl ?? ''}
                      onChange={(e) => setFaviconUrl(e.target.value.trim() || null)}
                      placeholder="https://"
                    />
                    <p className="text-xs text-muted-foreground">{t('storefront.steps.brand.faviconHint')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="rx-primary">{t('storefront.steps.brand.primaryLabel')}</Label>
                      <Input
                        id="rx-primary"
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="h-10 p-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rx-accent">{t('storefront.steps.brand.accentLabel')}</Label>
                      <Input
                        id="rx-accent"
                        type="color"
                        value={accentColor}
                        onChange={(e) => setAccentColor(e.target.value)}
                        className="h-10 p-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {subdomainCard('3')}

              <Card>
                <CardHeader>
                  <CardTitle>{t('storefront.steps.pixels.title')}</CardTitle>
                  <CardDescription>
                    {t('storefront.steps.pixels.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="rx-meta">{t('storefront.steps.pixels.metaLabel')}</Label>
                    <Input
                      id="rx-meta"
                      value={metaPixelId}
                      onChange={(e) => setMetaPixelId(e.target.value)}
                      placeholder={t('storefront.steps.pixels.metaPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rx-gtm">{t('storefront.steps.pixels.gtmLabel')}</Label>
                    <Input
                      id="rx-gtm"
                      value={gtmId}
                      onChange={(e) => setGtmId(e.target.value)}
                      placeholder={t('storefront.steps.pixels.gtmPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="rx-verification">
                      {t('storefront.steps.pixels.verificationLabel')}
                    </Label>
                    <Textarea
                      id="rx-verification"
                      rows={3}
                      className="font-mono text-xs"
                      value={siteVerifications}
                      onChange={(e) => setSiteVerifications(e.target.value)}
                      placeholder={t('storefront.steps.pixels.verificationPlaceholder')}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('storefront.steps.pixels.verificationHelp')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => void handleSave()} disabled={save.isPending}>
                  {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t('storefront.actions.saveDraft')}
                </Button>
                <Button onClick={() => void handlePublish()} disabled={publish.isPending || !slug}>
                  {publish.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t('storefront.actions.publish')}
                </Button>
                {profileQuery.data?.url ? (
                  <Button variant="ghost" asChild>
                    <a href={profileQuery.data.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t('storefront.actions.openSite')}
                    </a>
                  </Button>
                ) : null}
                {profileQuery.data?.funnelId ? (
                  <Button variant="ghost" asChild>
                    <a href={storeRoutes.FUNNEL_EDITOR(profileQuery.data.funnelId)}>
                      {t('storefront.actions.openFunnel')}
                    </a>
                  </Button>
                ) : null}
              </div>
            </>
          ) : (
            <>
              {subdomainCard('1')}

              <Card>
                <CardHeader>
                  <CardTitle>{t('storefront.steps.headless.title')}</CardTitle>
                  <CardDescription>
                    {t('storefront.steps.headless.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950">
                    <span className="font-medium">{t('storefront.steps.headless.legitScriptLabel')}</span>{' '}
                    {t('storefront.steps.headless.legitScriptBody', {
                      hostname: hostname ?? `your-brand.${apex}`,
                    })}{' '}
                    <Badge variant="outline">{t('storefront.steps.headless.legitScriptComingSoon')}</Badge>.
                  </div>

                  <ol className="list-decimal space-y-2 pl-5">
                    <li>{t('storefront.steps.headless.step1')}</li>
                    <li>
                      {t('storefront.steps.headless.step2Prefix')}{' '}
                      <a
                        href={`${DOCS_BASE}/developer-tools/rx/headless-sdk`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium underline"
                      >
                        {t('storefront.steps.headless.step2LinkText')}
                      </a>{' '}
                      — <code>tagada.rx</code> {t('storefront.steps.headless.step2Suffix')}
                    </li>
                    <li>
                      {t('storefront.steps.headless.step3Prefix')}{' '}
                      <a
                        href={`${DOCS_BASE}/developer-tools/rx/storefront-templates`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium underline"
                      >
                        {t('storefront.steps.headless.step3LinkText')}
                      </a>
                      {t('storefront.steps.headless.step3Suffix')}
                    </li>
                    <li>{t('storefront.steps.headless.step4')}</li>
                  </ol>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => void handleSave()} disabled={save.isPending}>
                      {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {t('storefront.actions.saveDraft')}
                    </Button>
                    <Button variant="ghost" asChild>
                      <a
                        href={`${DOCS_BASE}/developer-tools/rx/building-blocks`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        {t('storefront.steps.headless.buildingBlocks')}
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <RxStatementDescriptorCard storeId={storeId} />

          <RxSupportMailboxCard storeId={storeId} brandName={brandName} />
        </div>
      )}
    </RxPageShell>
  );
}
